<template>
  <transition name="slide-fade">
    <div v-if="showPrompt" class="pwa-prompt-container">
      <div class="glass-card">
        <div class="card-content">
          <div class="app-info">
            <v-avatar size="56" class="app-icon-wrapper elevation-4">
              <v-img src="/favicon.png"></v-img>
            </v-avatar>
            <div class="text-group">
              <h3 class="text-h6 font-weight-bold primary--text mb-1">Install Roster Pal</h3>
              <p class="text-caption grey--text text--darken-1 mb-0 line-height-tight">
                Get a faster, more reliable experience by adding us to your home screen.
              </p>
            </div>
          </div>
          <div class="action-group">
            <v-btn
              color="primary"
              depressed
              dark
              class="install-btn font-weight-bold"
              @click="installPWA"
            >
              Install Now
            </v-btn>
            <v-btn
              icon
              small
              class="close-btn-ghost"
              @click="dismissPrompt"
            >
              <v-icon size="20">mdi-close</v-icon>
            </v-btn>
          </div>
        </div>
      </div>

      <v-dialog v-model="showIOSInstructions" max-width="400">
        <v-card class="ios-install-card">
          <v-card-title class="text-h5 font-weight-bold primary--text">
            Add to Home Screen
          </v-card-title>
          <v-card-text class="pt-6 pb-2">
            <p class="mb-6">To install this app on your iPhone or iPad:</p>
            <div class="instruction-step mb-6">
              <v-avatar color="primary lighten-4" size="40" class="mr-4">
                <v-icon color="primary">mdi-export-variant</v-icon>
              </v-avatar>
              <div>
                <strong>Tap Share</strong>
                <p class="text-caption mb-0">Look for the share icon in your browser</p>
              </div>
            </div>
            <div class="instruction-step mb-4">
              <v-avatar color="primary lighten-4" size="40" class="mr-4">
                <v-icon color="primary">mdi-plus-box</v-icon>
              </v-avatar>
              <div>
                <strong>Add to Home Screen</strong>
                <p class="text-caption mb-0">Scroll down to find this option</p>
              </div>
            </div>
          </v-card-text>
          <v-card-actions class="pa-6 pt-0">
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              text
              class="font-weight-bold"
              @click="showIOSInstructions = false"
            >
              Got it
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </transition>
</template>

<script>
export default {
  name: "PWAInstallPrompt",
  data() {
    return {
      deferredPrompt: null,
      showPrompt: false,
      showIOSInstructions: false
    };
  },
  mounted() {
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    const checkDismissal = () => {
      const dismissedUntil = localStorage.getItem('pwa-prompt-dismissed-until');
      if (dismissedUntil) {
        const now = new Date().getTime();
        if (now < parseInt(dismissedUntil)) return true;
        localStorage.removeItem('pwa-prompt-dismissed-until');
      }
      return false;
    };

    if (isMobile && !checkDismissal()) {
      setTimeout(() => { this.showPrompt = true; }, 1500); 
    }

    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.deferredPrompt = e;
      if (!checkDismissal()) {
        setTimeout(() => { this.showPrompt = true; }, 1500);
      }
    });

    window.addEventListener('appinstalled', () => {
      this.showPrompt = false;
      this.deferredPrompt = null;
    });
  },
  methods: {
    dismissPrompt() {
      this.showPrompt = false;
      const fourteenDays = 10 * 24 * 60 * 60 * 1000;
      const expirationDate = new Date().getTime() + fourteenDays;
      localStorage.setItem('pwa-prompt-dismissed-until', expirationDate.toString());
    },
    async installPWA() {
      if (this.deferredPrompt) {
        this.deferredPrompt.prompt();
        const { outcome } = await this.deferredPrompt.userChoice;
        if (outcome === 'accepted') this.showPrompt = false;
        this.deferredPrompt = null;
        return;
      }
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      if (isIOS) {
        this.showIOSInstructions = true;
        return;
      }
      alert("Look for 'Add to Home Screen' in your browser menu.");
    }
  }
};
</script>

<style scoped>
.pwa-prompt-container {
  position: fixed;
  top: 90px;
  left: 50%;
  transform: translateX(-50%);
  width: 92%;
  max-width: 480px;
  z-index: 2000;
  pointer-events: none; /* Let clicks pass through to content behind if not on card */
}

.glass-card {
  pointer-events: auto; /* Re-enable clicks for the card itself */
  background: rgba(255, 255, 255, 0.7) !important;
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 12px 40px -4px rgba(106, 51, 153, 0.15), 
              0 0 0 1px rgba(106, 51, 153, 0.05);
  padding: 16px;
  overflow: hidden;
}

.card-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.app-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.app-icon-wrapper {
  background: white !important;
  border: 2px solid white;
}

.text-group {
  flex: 1;
}

.line-height-tight {
  line-height: 1.25 !important;
}

.action-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.install-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  letter-spacing: 0;
  padding: 0 20px !important;
  height: 44px !important;
}

.close-btn-ghost {
  color: rgba(0, 0, 0, 0.4) !important;
}

.ios-install-card {
  border-radius: 24px !important;
}

.instruction-step {
  display: flex;
  align-items: center;
}

/* Animations */
.slide-fade-enter-active {
  transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}
.slide-fade-leave-active {
  transition: all 0.4s cubic-bezier(0.7, 0, 0.84, 0);
}
.slide-fade-enter {
  transform: translate(-50%, -40px);
  opacity: 0;
}
.slide-fade-leave-to {
  transform: translate(-50%, -20px);
  opacity: 0;
}

@media (max-width: 600px) {
  .pwa-prompt-container {
    top: 80px;
  }
  .card-content {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  .action-group {
    justify-content: space-between;
  }
  .install-btn {
    flex: 1;
  }
}
</style>
